import type { StripeConnectInstance } from '@stripe/connect-js';

export type FetchClientSecret = () => Promise<string>;

export interface StripeConnectFactory {
  initialize(
    publishableKey: string,
    fetchClientSecret: FetchClientSecret,
  ): StripeConnectInstance;
}

export default defineNuxtPlugin(() => {
  let instance: StripeConnectInstance | null = null;

  async function initialize(
    publishableKey: string,
    fetchClientSecret: FetchClientSecret,
  ): Promise<StripeConnectInstance> {
    if (instance) return instance;

    const { loadConnectAndInitialize } =
      await import('@stripe/connect-js/pure');

    instance = loadConnectAndInitialize({
      publishableKey,
      fetchClientSecret,
      appearance: {
        overlays: 'dialog',
        variables: {
          colorPrimary: '#006686',
          colorBackground: '#FFFBFF',
          colorText: '#40484C',
          colorDanger: '#BA1A1A',
          fontFamily: 'Roboto, sans-serif',
        },
      },
    });

    return instance;
  }

  function destroy() {
    if (instance) {
      instance.logout();
      instance = null;
    }
  }

  return {
    provide: {
      stripeConnect: {
        initialize,
        destroy,
        getInstance: () => instance,
      },
    },
  };
});

declare module '#app' {
  interface NuxtApp {
    $stripeConnect: {
      initialize: (
        publishableKey: string,
        fetchClientSecret: FetchClientSecret,
      ) => Promise<StripeConnectInstance>;
      destroy: () => void;
      getInstance: () => StripeConnectInstance | null;
    };
  }
}
